import { CloudUpload, Eye, File, FileText, ImageIcon, X } from "lucide-react"
import type React from "react"
import { useRef, useState } from "react"
import {
	cn,
	getFormatMediaURL,
	parseAttributes,
	truncatePrefixSlash,
} from "../../lib/utils"
import {
	FileUploadConfig,
	type Attribute,
	type FIELD,
} from "../../types/form-types"
import { Button } from "../ui/button"
import { Card } from "../ui/card"

interface FileFieldProps {
	config: FIELD
	value?: File[] | string | null // Can be array of files or URL string from server
	onChange: (files: File[] | null) => void
	error?: string
	multiple?: boolean
	maxFiles?: number
	shouldDisable: boolean
}

export const FileField: React.FC<FileFieldProps> = ({
	config,
	value,
	onChange,
	error,
	multiple = false,
	maxFiles = 5,
	shouldDisable,
}) => {
	const [isDragOver, setIsDragOver] = useState(false)
	const fileInputRef = useRef<HTMLInputElement>(null)
	const { allowedFileTypes, maxFileSizeMB } = getFileUploadAttributes(
		config.attributes || [],
	)

	// Handle different value types (files array or URL string from server)
	const getDisplayValue = () => {
		if (!value) return { files: [], serverUrl: null }

		if (typeof value === "string") {
			// Server returned a URL
			return { files: [], serverUrl: value }
		}

		if (Array.isArray(value)) {
			// Local files array
			return { files: value, serverUrl: null }
		}

		return { files: [], serverUrl: null }
	}

	const { files: currentFiles, serverUrl } = getDisplayValue()
	const hasContent = currentFiles.length > 0 || serverUrl

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const selectedFiles = Array.from(e.target.files || [])

		if (multiple) {
			const newFiles = [...currentFiles, ...selectedFiles].slice(
				0,
				maxFiles,
			)
			onChange(newFiles.length > 0 ? newFiles : null)
		} else {
			// Single file mode - replace existing
			const file = selectedFiles[0]
			onChange(file ? [file] : null)
		}
	}

	const handleDrop = (e: React.DragEvent) => {
		e.preventDefault()
		setIsDragOver(false)

		const droppedFiles = Array.from(e.dataTransfer.files)

		if (multiple) {
			const newFiles = [...currentFiles, ...droppedFiles].slice(
				0,
				maxFiles,
			)
			onChange(newFiles.length > 0 ? newFiles : null)
		} else {
			const file = droppedFiles[0]
			onChange(file ? [file] : null)
		}
	}

	const handleDragOver = (e: React.DragEvent) => {
		e.preventDefault()
		setIsDragOver(true)
	}

	const handleDragLeave = (e: React.DragEvent) => {
		e.preventDefault()
		const rect = e.currentTarget.getBoundingClientRect()
		const x = e.clientX
		const y = e.clientY

		if (
			x < rect.left ||
			x > rect.right ||
			y < rect.top ||
			y > rect.bottom
		) {
			setIsDragOver(false)
		}
	}

	const handleBrowse = () => {
		fileInputRef.current?.click()
	}

	const handleRemove = (indexToRemove?: number) => {
		if (serverUrl) {
			// Remove server URL - this will trigger a re-upload
			onChange([])
		} else if (typeof indexToRemove === "number") {
			// Remove specific file
			const newFiles = currentFiles.filter(
				(_, index) => index !== indexToRemove,
			)
			onChange(newFiles.length > 0 ? newFiles : [])
		} else {
			// Remove all files
			onChange([])
		}

		if (fileInputRef.current) {
			fileInputRef.current.value = ""
		}
	}

	const getFileIcon = (file: File) => {
		if (file.type.startsWith("image/")) {
			return <ImageIcon className="h-5 w-5 text-blue-500" />
		} else if (file.type === "application/pdf") {
			return <FileText className="h-5 w-5 text-red-500" />
		}
		return <File className="text-muted-foreground h-5 w-5" />
	}

	const getUrlFileIcon = (url: string) => {
		const extension = url.split(".").pop()?.toLowerCase()
		if (["jpg", "jpeg", "png", "gif", "webp"].includes(extension || "")) {
			return <ImageIcon className="h-5 w-5 text-blue-500" />
		} else if (extension === "pdf") {
			return <FileText className="h-5 w-5 text-red-500" />
		}
		return <File className="text-muted-foreground h-5 w-5" />
	}

	const getFileName = (url: string) => {
		return url.split("/").pop() || "Uploaded file"
	}

	const formatFileSize = (bytes: number) => {
		if (bytes === 0) return "0 Bytes"
		const k = 1024
		const sizes = ["Bytes", "KB", "MB", "GB"]
		const i = Math.floor(Math.log(bytes) / Math.log(k))
		return (
			Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2)) +
			" " +
			sizes[i]
		)
	}

	const canAddMore = multiple && currentFiles.length < maxFiles && !serverUrl

	return (
		<div className="df-field space-y-3">
			{/* Upload area - show if no content or if multiple files allowed and can add more */}
			{(!hasContent || canAddMore) && (
				<Card
					className={cn(
						"hover:border-primary bg-muted/50 flex w-fit cursor-pointer items-center gap-5 rounded-xl border-2 border-dashed px-7 py-5 text-center transition-all duration-200",
						isDragOver && "border-primary bg-primary/5",
						error && "border-destructive",
						hasContent && "p-4", // Smaller padding when content exists
					)}
					onDrop={handleDrop}
					onDragOver={handleDragOver}
					onDragLeave={handleDragLeave}
					onClick={handleBrowse}
				>
					<CloudUpload
						strokeWidth={1}
						className={cn(
							"text-primary m-auto h-12 w-12",
							hasContent && "mb-2 h-6 w-6", // Smaller icon when content exists
						)}
					/>
					<div className="space-y-2">
						<div className="flex items-center">
							<p
								className={cn(
									"text-foreground font-bold",
									hasContent ? "text-sm" : "text-base",
								)}
							>
								{hasContent
									? serverUrl
										? "Replace file"
										: `Add more files (${currentFiles.length}/${maxFiles})`
									: "Drag & drop files or"}
							</p>
							{/* {!hasContent && (
								<p className="text-muted-foreground text-sm">
									click to browse
								</p>
							)} */}

							<Button
								type="button"
								variant="ghost"
								size="sm"
								className="text-primary pointer-events-none bg-transparent underline"
							>
								{serverUrl
									? "Replace File"
									: `Choose File${multiple ? "s" : ""}`}
							</Button>
						</div>
						{!hasContent && (
							<p className="text-muted-foreground text-xs">
								Supported formats:{" "}
								{allowedFileTypes?.join(", ")} (Max{" "}
								{maxFileSizeMB}MB)
								{multiple && ` • Up to ${maxFiles} files`}
							</p>
						)}
					</div>
				</Card>
			)}

			{/* Display server URL */}
			{serverUrl && (
				<Card className="p-4">
					<div className="flex items-center justify-between">
						<div className="flex min-w-0 flex-1 items-center space-x-3">
							{getUrlFileIcon(serverUrl)}
							<div className="min-w-0 flex-1">
								<p className="text-foreground truncate text-sm font-medium">
									{getFileName(serverUrl)}
								</p>
								<p className="text-muted-foreground text-xs">
									Uploaded file
								</p>
							</div>
						</div>
						<div className="flex items-center gap-2">
							<Button
								type="button"
								variant="ghost"
								size="sm"
								onClick={e => {
									e.stopPropagation()
									window.open(
										getFormatMediaURL(
											truncatePrefixSlash(serverUrl),
										),
										"_blank",
									)
								}}
								className="text-primary hover:bg-primary/10"
							>
								<Eye className="h-4 w-4" />
							</Button>
							{!shouldDisable && (
								<Button
									type="button"
									variant="ghost"
									size="sm"
									onClick={e => {
										e.stopPropagation()
										handleRemove()
									}}
									className="text-destructive hover:bg-destructive/10 hover:text-destructive flex-shrink-0"
								>
									<X className="h-4 w-4" />
								</Button>
							)}
						</div>
					</div>
				</Card>
			)}

			{/* Display uploaded files */}
			{currentFiles.length > 0 && (
				<div className="space-y-2">
					{currentFiles.map((file, index) => (
						<Card
							key={`${file.name}-${file.size}-${index}`}
							className="p-4"
						>
							<div className="flex items-center justify-between">
								<div className="flex min-w-0 flex-1 items-center space-x-3">
									{getFileIcon(file)}
									<div className="min-w-0 flex-1">
										<p className="text-foreground truncate text-sm font-medium">
											{file.name}
										</p>
										<p className="text-muted-foreground text-xs">
											{formatFileSize(file.size)}
										</p>
									</div>
								</div>
								<Button
									type="button"
									variant="ghost"
									size="sm"
									onClick={e => {
										e.stopPropagation()
										handleRemove(index)
									}}
									className="text-destructive hover:bg-destructive/10 hover:text-destructive flex-shrink-0"
								>
									<X className="h-4 w-4" />
								</Button>
							</div>
						</Card>
					))}

					{/* Clear all button for multiple files */}
					{multiple && currentFiles.length > 1 && (
						<Button
							type="button"
							variant="outline"
							size="sm"
							onClick={() => handleRemove()}
							className="text-destructive hover:bg-destructive/10 hover:text-destructive w-full"
						>
							Remove All Files
						</Button>
					)}
				</div>
			)}

			<input
				// {...formField}
				ref={fileInputRef}
				type="file"
				accept={allowedFileTypes?.join(",")}
				onChange={handleFileChange}
				className="hidden"
				// required={config.is_required && !hasContent}
				multiple={multiple}
				placeholder="file upload"
				disabled={shouldDisable}
			/>
			{/*
			{error && (
				<p className="text-destructive flex items-center gap-1 text-sm font-medium">
					<span className="bg-destructive inline-block h-1 w-1 rounded-full"></span>
					{error}
				</p>
			)} */}
		</div>
	)
}

function getFileUploadAttributes(attributes: Attribute[]) {
	const props: {
		allowedFileTypes?: string[]
		maxFileSizeMB?: number
	} = {}

	const parsedAttributes = parseAttributes(attributes)

	Object.entries(parsedAttributes).forEach(([key, value]) => {
		switch (key) {
			case FileUploadConfig.MaxFileSizeMB:
				props.maxFileSizeMB = value as number
				break
			case FileUploadConfig.AllowedFileTypes:
				props.allowedFileTypes = value as string[]
				break
			default:
				break
		}
	})
	return props
}
